// src/services/MigrationService.ts
import { Competition, Solution, User } from '../models';
import { DataMigrator } from '../utils/DataMigrator';
import { SchemaComparator } from '../utils/SchemaComparator';
import { LocalStorageDataService } from './LocalStorageDataService';

/**
 * データマイグレーションを管理するサービスクラス
 */
export class MigrationService {
  private dataService: LocalStorageDataService;
  
  constructor() {
    this.dataService = new LocalStorageDataService();
  }
  
  /**
   * コンペティションデータをマイグレーションする
   * @param csvData 新しいCSVデータ
   * @returns マイグレーション結果
   */
  async migrateCompetitions(csvData: string): Promise<MigrationResult> {
    try {
      // 既存のデータを取得
      const competitions = await this.dataService.getCompetitions();
      
      // ヘッダー行を解析
      const headers = csvData.trim().split('\n')[0];
      const newHeaders = headers.split(',');
      
      // 既存のデータからヘッダーを取得
      const oldHeaders = competitions.length > 0 ? Object.keys(competitions[0]) : [];
      
      // スキーマ変更を検出
      const schemaChange = SchemaComparator.detectCompetitionSchemaChanges(oldHeaders, newHeaders);
      
      if (schemaChange.hasCriticalChanges) {
        return {
          success: false,
          error: `クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`
        };
      }
      
      // データをマイグレーション
      const migratedCompetitions = DataMigrator.migrateCompetitions(competitions, csvData);
      
      // 更新されたデータを保存
      await this.dataService.updateCompetitions(migratedCompetitions);
      
      return {
        success: true,
        addedCount: migratedCompetitions.length - competitions.length,
        updatedCount: Math.min(competitions.length, migratedCompetitions.length),
        schemaChanges: schemaChange
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '不明なエラーが発生しました'
      };
    }
  }
  
  /**
   * ソリューションデータをマイグレーションする
   * @param csvData 新しいCSVデータ
   * @returns マイグレーション結果
   */
  async migrateSolutions(csvData: string): Promise<MigrationResult> {
    try {
      // 既存のデータを取得
      const solutions = await this.dataService.getSolutions();
      
      // ヘッダー行を解析
      const headers = csvData.trim().split('\n')[0];
      const newHeaders = headers.split(',');
      
      // 既存のデータからヘッダーを取得
      const oldHeaders = solutions.length > 0 ? Object.keys(solutions[0]) : [];
      
      // スキーマ変更を検出
      const schemaChange = SchemaComparator.detectSolutionSchemaChanges(oldHeaders, newHeaders);
      
      if (schemaChange.hasCriticalChanges) {
        return {
          success: false,
          error: `クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`
        };
      }
      
      // データをマイグレーション
      const migratedSolutions = DataMigrator.migrateSolutions(solutions, csvData);
      
      // 更新されたデータを保存
      await this.dataService.updateSolutions(migratedSolutions);
      
      return {
        success: true,
        addedCount: migratedSolutions.length - solutions.length,
        updatedCount: Math.min(solutions.length, migratedSolutions.length),
        schemaChanges: schemaChange
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '不明なエラーが発生しました'
      };
    }
  }
  
  /**
   * ユーザーデータをマイグレーションする
   * @param csvData 新しいCSVデータ
   * @returns マイグレーション結果
   */
  async migrateUsers(csvData: string): Promise<MigrationResult> {
    try {
      // 既存のデータを取得
      const users = await this.dataService.getUsers();
      
      // ヘッダー行を解析
      const headers = csvData.trim().split('\n')[0];
      const newHeaders = headers.split(',');
      
      // 既存のデータからヘッダーを取得
      const oldHeaders = users.length > 0 ? Object.keys(users[0]) : [];
      
      // スキーマ変更を検出
      const schemaChange = SchemaComparator.detectUserSchemaChanges(oldHeaders, newHeaders);
      
      if (schemaChange.hasCriticalChanges) {
        return {
          success: false,
          error: `クリティカルなスキーマ変更が検出されました: ${schemaChange.criticalChanges}`
        };
      }
      
      // データをマイグレーション
      const migratedUsers = DataMigrator.migrateUsers(users, csvData);
      
      // 更新されたデータを保存
      await this.dataService.updateUsers(migratedUsers);
      
      return {
        success: true,
        addedCount: migratedUsers.length - users.length,
        updatedCount: Math.min(users.length, migratedUsers.length),
        schemaChanges: schemaChange
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : '不明なエラーが発生しました'
      };
    }
  }
}

/**
 * マイグレーション結果を表すインターフェース
 */
export interface MigrationResult {
  success: boolean;
  error?: string;
  addedCount?: number;
  updatedCount?: number;
  schemaChanges?: any;
}
